/* The React binding over session.ts. All of the auth module's React lives here.

   The provider owns no auth logic — it mirrors session.ts's state into React
   state and forwards three intents (start, finish, end). Keeping the flow itself
   framework-free is what lets the API client call `getToken()` from outside any
   component, and lets the whole thing be reasoned about without React's
   lifecycle in the way. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  beginSignIn,
  completeCallback,
  isCallbackUrl,
  onSessionChange,
  pendingCallback,
  restoreSession,
  signOut as endSession,
  stopRefreshTimer,
} from './session'

export type AuthStatus =
  /** Deciding: restoring a session, or first paint. */
  | 'loading'
  /** No session. The sign-in page is the right thing to render. */
  | 'signed-out'
  /** A redirect is being prepared, or the callback is being exchanged. */
  | 'signing-in'
  | 'signed-in'
  /** A sign-in attempt failed. `error` says how; signing in again is allowed. */
  | 'error'

export interface AuthContextValue {
  status: AuthStatus
  /** Display name for the header, from token claims. Null unless signed in. */
  user: string | null
  /** Human-readable failure from the last attempt, or null. */
  error: string | null
  signIn: () => void
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  return typeof error === 'string' ? error : 'Sign-in failed for an unknown reason.'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    /* React 19 StrictMode runs this effect twice in dev (mount → cleanup →
       mount). What actually happens on a callback, traced rather than assumed:

         1. Mount 1 sees isCallbackUrl() and calls completeCallback(). runCallback
            runs synchronously as far as its `await postToken(...)` — and its
            `history.replaceState(null, '', '/')` is in that synchronous prefix.
         2. Cleanup sets `cancelled`, so mount 1 will not write state.
         3. Mount 2 evaluates isCallbackUrl() against a pathname that is now '/'.
            It is FALSE. Mount 2 never calls completeCallback(); it falls to the
            branch below.

       So the code is exchanged exactly once — not because completeCallback() is
       memoized (nothing calls it twice here) but because mount 2 no longer
       recognises the URL. The memo still matters as the guarantee that any other
       double call is safe, and `cancelled` still matters so mount 1 does not
       write into an unmounted tree.

       The consequence to watch is on the failure path: mount 1's `.catch`
       returns early on `cancelled`, so without the pendingCallback() re-attach
       below a failed exchange would report nothing at all — the user would get
       the sign-in page with `error` still null and the reason discarded.
       Production mounts once and never takes that path. */
    let cancelled = false

    // session.ts is the source of truth: a background refresh that fails ends
    // the session without anyone calling signOut, and this is how the UI hears.
    const unsubscribe = onSessionChange((name) => {
      if (cancelled) return
      if (name) {
        setUser(name)
        setError(null)
        setStatus('signed-in')
      } else {
        setUser(null)
        setStatus('signed-out')
      }
    })

    const report = (exchange: Promise<string>) => {
      setStatus('signing-in')
      exchange
        .then((name) => {
          if (cancelled) return
          setUser(name)
          setStatus('signed-in')
        })
        .catch((cause: unknown) => {
          if (cancelled) return
          setError(messageOf(cause))
          setStatus('error')
        })
    }

    /* An exchange this page load already started, which only StrictMode's second
       mount can see. Attaching to it is not a second exchange — it is the same
       memoized promise — so the spent authorization code is never replayed. */
    const started = pendingCallback()

    if (isCallbackUrl()) {
      report(completeCallback())
    } else if (started) {
      report(started)
    } else {
      restoreSession()
        .then((name) => {
          if (cancelled) return
          setUser(name)
          setStatus(name ? 'signed-in' : 'signed-out')
        })
        .catch(() => {
          if (cancelled) return
          setStatus('signed-out')
        })
    }

    return () => {
      cancelled = true
      unsubscribe()
      /* Drop the refresh timer with the provider. A remount re-arms it, because
         restoreSession() reschedules from the live token's expiry — which is
         precisely why it does that even when it has nothing to refresh. */
      stopRefreshTimer()
    }
  }, [])

  const signIn = useCallback(() => {
    setError(null)
    setStatus('signing-in')
    // Resolves only if the redirect did not happen; on success the page is
    // already leaving, so there is nothing to do in a `then`.
    beginSignIn().catch((cause: unknown) => {
      setError(messageOf(cause))
      setStatus('error')
    })
  }, [])

  const signOut = useCallback(() => {
    setError(null)
    // Fires the listener above, which flips the app back to the sign-in page.
    endSession()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, signIn, signOut }),
    [status, user, error, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** The session, from anywhere inside `<AuthProvider>`. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>')
  return value
}
